import { CSSProperties, useState, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { ContactDetails, ValidationError } from '../../types';
import { LEAD_SOURCES, US_STATES } from '../../constants';
import { validateContactDetails } from '../../utils/validation';
import Input from '../Input';
import Select from '../Select';
import Textarea from '../Textarea';
import Modal from '../Modal';
import Button from '../Button';

interface Step4Props {
  contactDetails: ContactDetails;
  onContactDetailsChange: (details: ContactDetails) => void;
  onSubmit: () => void;
  serviceId: string;
}

export default function Step4DetailsForm({
  contactDetails,
  onContactDetailsChange,
  onSubmit,
  serviceId
}: Step4Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showWarning, setShowWarning] = useState(false);

  const normalizeNameInput = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const lower = trimmed.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const handleChange = (field: keyof ContactDetails, rawValue: any) => {
    let value = rawValue;

    if ((field === 'firstName' || field === 'lastName') && typeof rawValue === 'string') {
      value = normalizeNameInput(rawValue);
    }

    const updated: ContactDetails = {
      ...contactDetails,
      [field]: value
    };

    if (field === 'referredBy' && value !== 'other') {
      updated.referredByOther = '';
    }

    onContactDetailsChange(updated);

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Validate
    const validationErrors = validateContactDetails(contactDetails, serviceId);
    if (validationErrors.length > 0) {
      const errorMap: Record<string, string> = {};
      validationErrors.forEach((err: ValidationError) => {
        errorMap[err.field] = err.message;
      });
      setErrors(errorMap);
      return;
    }

    // Check if user sells benefits/senior life
    if (!isSimplified && !contactDetails.sellsBenefitsSeniorLife) {
      setShowWarning(true);
      return;
    }

    onSubmit();
  };

  const handleWarningProceed = () => {
    setShowWarning(false);
    onSubmit();
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
  };

  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xl)',
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const headerStyles: CSSProperties = {
    textAlign: 'center',
    marginBottom: 'var(--spacing-md)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const subtitleStyles: CSSProperties = {
    fontSize: '1.125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6
  };

  const formStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-lg)'
  };

  const rowStyles: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--spacing-md)'
  };

  const isKickoff = serviceId === 'kickoff';
  const isSupport = serviceId === 'support';
  const isSimplified = isKickoff || isSupport;
  const requireLastName = true;
  const requireAgency = true;
  const currentCrmLabel =
    serviceId === 'demo'
      ? 'Current CRM or Agency Management System'
      : 'Current CRM';

  const checkboxContainerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    background: 'var(--na-gray-50)',
    borderRadius: 'var(--radius-md)'
  };

  const checkboxLabelStyles: CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--color-text)',
    cursor: 'pointer'
  };

  const requiredNoteStyles: CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic'
  };

  return (
    <div style={containerStyles}>
      <motion.div
        style={headerStyles}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 style={titleStyles}>Your Details</h1>
        <p style={subtitleStyles}>
          Please provide your information to complete the booking
        </p>
        <p style={requiredNoteStyles}>Fields marked with * are required</p>
      </motion.div>

      <form style={formStyles} onSubmit={handleSubmit}>
        <div style={rowStyles}>
          <Input
            label="First Name"
            type="text"
            required
            value={contactDetails.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={errors.firstName}
          />
          <Input
            label="Last Name"
            type="text"
            required={requireLastName}
            value={contactDetails.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={errors.lastName}
          />
        </div>

        <Input
          label="Email"
          type="email"
          required
          value={contactDetails.email}
          onChange={(e) => handleChange('email', e.target.value)}
          error={errors.email}
        />

        <div style={rowStyles}>
          <Input
            label="Agency Name"
            type="text"
            required={requireAgency}
            value={contactDetails.agency}
            onChange={(e) => handleChange('agency', e.target.value)}
            error={errors.agency}
          />
          {!isSimplified && (
            <Select
              label="State"
              required
              value={contactDetails.state}
              onChange={(e) => handleChange('state', e.target.value)}
              options={[
                { value: '', label: 'Select state...' },
                ...US_STATES.map((state) => ({ value: state, label: state }))
              ]}
              error={errors.state}
            />
          )}
        </div>

        {!isSimplified && (
          <>
            <div style={rowStyles}>
              <Input
                label="Agency Phone"
                type="tel"
                value={contactDetails.agencyPhone || ''}
                onChange={(e) => handleChange('agencyPhone', e.target.value)}
                error={errors.agencyPhone}
                helpText="Format: (123) 456-7890"
              />
              <Input
                label="Agency Website"
                type="text"
                value={contactDetails.agencyWebsite || ''}
                onChange={(e) => handleChange('agencyWebsite', e.target.value)}
                error={errors.agencyWebsite}
              />
            </div>

            <div style={rowStyles}>
              <Input
                label="Agency Size"
                type="number"
                min="1"
                value={contactDetails.agencySize || ''}
                onChange={(e) => handleChange('agencySize', e.target.value ? parseInt(e.target.value) : undefined)}
                error={errors.agencySize}
                helpText="Number of users"
              />
              <Input
                label="How Many Will Attend"
                type="number"
                min="1"
                value={contactDetails.attendeeCount || ''}
                onChange={(e) => handleChange('attendeeCount', e.target.value ? parseInt(e.target.value) : undefined)}
                error={errors.attendeeCount}
              />
            </div>

            <Input
              label={currentCrmLabel}
              type="text"
              value={contactDetails.currentCrm || ''}
              onChange={(e) => handleChange('currentCrm', e.target.value)}
              helpText="What system are you currently using?"
            />

            <Textarea
              label="What Problems Does Your Agency Need to Solve?"
              value={contactDetails.problems || ''}
              onChange={(e) => handleChange('problems', e.target.value)}
              error={errors.problems}
              helpText="Maximum 2000 characters"
              maxLength={2000}
            />

            <motion.div
              style={checkboxContainerStyles}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <input
                type="checkbox"
                id="sellsBenefitsSeniorLife"
                checked={contactDetails.sellsBenefitsSeniorLife}
                onChange={(e) => handleChange('sellsBenefitsSeniorLife', e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <label htmlFor="sellsBenefitsSeniorLife" style={checkboxLabelStyles}>
                My agency sells benefits, senior, or life policies.
              </label>
            </motion.div>
          </>
        )}

        {!isSimplified && (
          <>
            <Select
              label="How Did You Hear About Us?"
              required
              value={contactDetails.referredBy}
              onChange={(e) => handleChange('referredBy', e.target.value)}
              options={LEAD_SOURCES}
              error={errors.referredBy}
            />

            {(contactDetails.referredBy === 'other' || contactDetails.referredBy === 'personal_referral') && (
              <Input
                label={contactDetails.referredBy === 'personal_referral' ? 'Who referred you?' : 'Please specify'}
                type="text"
                required
                value={contactDetails.referredByOther || ''}
                onChange={(e) => handleChange('referredByOther', e.target.value)}
                error={errors.referredByOther}
              />
            )}
          </>
        )}

        {isSupport && (
          <Textarea
            label="What Problems Does Your Agency Need to Solve?"
            value={contactDetails.problems || ''}
            onChange={(e) => handleChange('problems', e.target.value)}
            error={errors.problems}
            required
            helpText="Tell us what you need help with"
            maxLength={2000}
          />
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button type="submit" variant="primary" fullWidth>
            Complete Booking
          </Button>
        </motion.div>
      </form>

      {/* Warning Modal for non-benefits/senior-life agencies */}
      <Modal
        isOpen={showWarning}
        onClose={handleWarningCancel}
        title="Heads Up: Different Focus"
        actions={
          <>
            <Button onClick={handleWarningCancel} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleWarningProceed} variant="primary">
              Proceed Anyway
            </Button>
          </>
        }
      >
        <p>
          NextAgency is designed for health and senior-life agencies. If your agency focuses on
          other products, some features may not apply.
        </p>
      </Modal>
    </div>
  );
}
